/**
 * Widget Store Drawer - Bottom drawer showing available widgets
 * Widgets can be dragged from here to the dashboard
 */

import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Package } from 'lucide-react'
import { useDashboardLayoutStore } from '@/stores/useDashboardLayoutStore'
import { getAvailableWidgets, WidgetDefinition } from '../widget-registry'
import WidgetCategorySection from './WidgetCategorySection'

interface WidgetStoreDrawerProps {
    activeWidgetIds: string[]
    initialWidgetIds: string[] // IDs of widgets defined in initialWidgets (have components)
    onAddWidget: (widgetId: string) => void
}

const WidgetStoreDrawer: React.FC<WidgetStoreDrawerProps> = ({
    activeWidgetIds,
    initialWidgetIds,
    onAddWidget,
}) => {
    const { t } = useTranslation()
    const {
        isStoreOpen,
        setIsStoreOpen,
        currentDepartment,
        setAvailableWidgetCount,
    } = useDashboardLayoutStore()

    // Get available widgets - only show widgets that exist in initialWidgets (have components)
    const availableWidgets = useMemo(() => {
        if (!currentDepartment) return []
        const registryWidgets = getAvailableWidgets(
            currentDepartment,
            activeWidgetIds
        )
        // Filter to only include widgets that are in initialWidgets (have actual components)
        return registryWidgets.filter((w) => initialWidgetIds.includes(w.id))
    }, [currentDepartment, activeWidgetIds, initialWidgetIds])

    // Categorize widgets
    const { commonWidgets, departmentWidgets } = useMemo(() => {
        const common: WidgetDefinition[] = []
        const department: WidgetDefinition[] = []

        availableWidgets.forEach((widget) => {
            if (widget.category === 'common') {
                common.push(widget)
            } else {
                department.push(widget)
            }
        })

        return { commonWidgets: common, departmentWidgets: department }
    }, [availableWidgets])

    // Update available widget count in store for header badge
    useEffect(() => {
        setAvailableWidgetCount(availableWidgets.length)
    }, [availableWidgets.length, setAvailableWidgetCount])

    if (!isStoreOpen) return null

    return (
        <div
            className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t border-gray-200 shadow-lg
                transform transition-transform duration-300 ease-out"
            style={{ maxHeight: '40vh' }}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold text-gray-900">
                        {t('dashboard.widget-store')}
                    </h3>
                    <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                        {availableWidgets.length} {t('dashboard.available')}
                    </span>
                </div>
                <button
                    onClick={() => setIsStoreOpen(false)}
                    className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                >
                    <X className="w-5 h-5 text-gray-500" />
                </button>
            </div>

            {/* Content */}
            <div
                className="p-4 overflow-y-auto"
                style={{ maxHeight: 'calc(40vh - 60px)' }}
            >
                {availableWidgets.length === 0 ? (
                    <div className="text-center py-8">
                        <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                        <p className="text-gray-500">
                            {t('dashboard.all-widgets-added')}
                        </p>
                        <p className="text-sm text-gray-400 mt-1">
                            {t('dashboard.drag-to-remove-hint')}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Common widgets section */}
                        <WidgetCategorySection
                            title="Common"
                            titleKey="dashboard.common-widgets"
                            widgets={commonWidgets}
                            defaultOpen={true}
                            onAddWidget={onAddWidget}
                        />

                        {/* Department specific widgets section */}
                        <WidgetCategorySection
                            title="Department"
                            titleKey="dashboard.department-widgets"
                            widgets={departmentWidgets}
                            defaultOpen={true}
                            onAddWidget={onAddWidget}
                        />
                    </div>
                )}
            </div>

            {/* Drag hint */}
            {availableWidgets.length > 0 && (
                <div className="px-4 py-2 bg-blue-50 border-t border-blue-100">
                    <p className="text-xs text-blue-600 text-center">
                        {t('dashboard.drag-widget-hint')}
                    </p>
                </div>
            )}
        </div>
    )
}

export default WidgetStoreDrawer
